import { Box, Button, Heading, HStack, Spinner, Text, VStack } from '@chakra-ui/react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { NumberInput } from '~/components/inputs/NumberInput';
import { toaster } from '~/components/ui/toaster';
import { useAppSettingsLoader } from '~/hooks/useAppSettings';
import type { BondInterestRateSettings } from '~/types/app-settings';
import { BOND_RATE_CONSTRAINTS, DEFAULT_APP_SETTINGS } from '~/types/app-settings';

export const AppSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { settings, updateBondInterestRates, resetToDefaults, isLoading, hasLoaded } =
    useAppSettingsLoader();

  const [localRates, setLocalRates] = useState<BondInterestRateSettings>(
    settings.bondInterestRates
  );
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Sync local state when settings change externally (e.g., after loading from DB)
  useEffect(() => {
    setLocalRates(settings.bondInterestRates);
    setHasChanges(false);
  }, [settings.bondInterestRates]);

  const handleRateChange = (field: keyof BondInterestRateSettings, value: string) => {
    const numValue = parseFloat(value);
    if (Number.isNaN(numValue)) {
      // Allow empty string temporarily for editing
      setLocalRates((prev) => ({
        ...prev,
        [field]: parseFloat(value),
      }));
      setHasChanges(true);
      return;
    }

    setLocalRates((prev) => ({
      ...prev,
      [field]: numValue,
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    // Validate
    const rates: BondInterestRateSettings = {
      startRate:
        typeof localRates.startRate === 'string'
          ? parseFloat(localRates.startRate)
          : localRates.startRate,
      incrementRate:
        typeof localRates.incrementRate === 'string'
          ? parseFloat(localRates.incrementRate)
          : localRates.incrementRate,
      maxRate:
        typeof localRates.maxRate === 'string'
          ? parseFloat(localRates.maxRate)
          : localRates.maxRate,
    };

    // Check for NaN values
    if (
      Number.isNaN(rates.startRate) ||
      Number.isNaN(rates.incrementRate) ||
      Number.isNaN(rates.maxRate)
    ) {
      toaster.create({
        title: 'Invalid Settings',
        description: 'All fields must be valid numbers',
        type: 'error',
        duration: 5000,
      });
      return;
    }

    if (rates.startRate > rates.maxRate) {
      toaster.create({
        title: 'Invalid Settings',
        description: 'Start rate cannot be greater than max rate',
        type: 'error',
        duration: 5000,
      });
      return;
    }

    if (rates.incrementRate < 0) {
      toaster.create({
        title: 'Invalid Settings',
        description: 'Increment rate cannot be negative',
        type: 'error',
        duration: 5000,
      });
      return;
    }

    // Save to database
    setIsSaving(true);
    await updateBondInterestRates(rates);
    setIsSaving(false);
    setHasChanges(false);

    toaster.create({
      title: 'Settings Saved',
      description: 'Bond interest rate settings updated successfully',
      type: 'success',
      duration: 3000,
    });
  };

  const handleReset = async () => {
    setIsSaving(true);
    await resetToDefaults();
    setLocalRates(DEFAULT_APP_SETTINGS.bondInterestRates);
    setIsSaving(false);
    setHasChanges(false);

    toaster.create({
      title: 'Reset to Defaults',
      description: 'Settings reset to default values',
      type: 'info',
      duration: 3000,
    });
  };

  // Calculate example escalation
  const startRateNum =
    typeof localRates.startRate === 'string'
      ? parseFloat(localRates.startRate)
      : localRates.startRate;
  const incrementNum =
    typeof localRates.incrementRate === 'string'
      ? parseFloat(localRates.incrementRate)
      : localRates.incrementRate;
  const maxRateNum =
    typeof localRates.maxRate === 'string' ? parseFloat(localRates.maxRate) : localRates.maxRate;
  const secondPaymentRate =
    Number.isNaN(startRateNum) || Number.isNaN(incrementNum) ? 0 : startRateNum + incrementNum;

  // Show loading state while fetching settings
  if (isLoading || !hasLoaded) {
    return (
      <Box maxWidth="800px" mx="auto" p={8}>
        <VStack align="center" justify="center" minH="200px" gap={4}>
          <Spinner size="lg" />
          <Text color="gray.600">Loading settings...</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box maxWidth="800px" mx="auto" p={8}>
      <VStack align="stretch" gap={6}>
        {/* Header */}
        <HStack justify="space-between">
          <Heading size="lg">App Settings</Heading>
          <Button variant="ghost" onClick={() => navigate('/')}>
            Back to Home
          </Button>
        </HStack>

        {/* Bond Interest Rate Settings Section */}
        <Box p={6} borderWidth="1px" borderRadius="md" bg="white">
          <VStack align="stretch" gap={4}>
            <Heading size="md">Bond Interest Rate Baseline</Heading>
            <Text fontSize="sm" color="gray.600">
              Default interest rate settings for all projects. Individual projects and bonds can
              override these values.
            </Text>

            {/* Start Rate */}
            <Box>
              <NumberInput
                label="Starting Interest Rate (%)"
                value={localRates.startRate.toString()}
                onChange={(val) => handleRateChange('startRate', val)}
                min={BOND_RATE_CONSTRAINTS.MIN_RATE}
                max={BOND_RATE_CONSTRAINTS.MAX_RATE}
                step={BOND_RATE_CONSTRAINTS.STEP}
                precision={BOND_RATE_CONSTRAINTS.DECIMAL_PLACES}
                helperText="Initial interest rate for first payment (e.g., 3.0 for 3.0%)"
              />
            </Box>

            {/* Increment Rate */}
            <Box>
              <NumberInput
                label="Increment Rate per Payment (%)"
                value={localRates.incrementRate.toString()}
                onChange={(val) => handleRateChange('incrementRate', val)}
                min={0}
                max={1}
                step={BOND_RATE_CONSTRAINTS.STEP}
                precision={BOND_RATE_CONSTRAINTS.DECIMAL_PLACES}
                helperText="Rate increase per payment period (e.g., 0.05 for 0.05% increase every 6 months)"
              />
            </Box>

            {/* Max Rate */}
            <Box>
              <NumberInput
                label="Maximum Interest Rate (%)"
                value={localRates.maxRate.toString()}
                onChange={(val) => handleRateChange('maxRate', val)}
                min={BOND_RATE_CONSTRAINTS.MIN_RATE}
                max={BOND_RATE_CONSTRAINTS.MAX_RATE}
                step={BOND_RATE_CONSTRAINTS.STEP}
                precision={BOND_RATE_CONSTRAINTS.DECIMAL_PLACES}
                helperText="Cap for interest rate escalation (e.g., 5.0 for 5.0%)"
              />
            </Box>

            {/* Example Calculation */}
            <Box p={3} bg="blue.50" borderRadius="md">
              <Text fontSize="sm" fontWeight="semibold" mb={1}>
                Example Escalation:
              </Text>
              <Text fontSize="xs" color="gray.700">
                Payment 1: {Number.isNaN(startRateNum) ? '?' : startRateNum.toFixed(3)}% → Payment
                2: {Number.isNaN(secondPaymentRate) ? '?' : secondPaymentRate.toFixed(3)}% → ... →
                Max: {Number.isNaN(maxRateNum) ? '?' : maxRateNum.toFixed(3)}%
              </Text>
            </Box>

            {/* Action Buttons */}
            <HStack justify="flex-end" gap={2}>
              <Button variant="outline" onClick={handleReset} disabled={isSaving}>
                Reset to Defaults
              </Button>
              <Button
                colorPalette="blue"
                onClick={handleSave}
                disabled={!hasChanges || isSaving}
                loading={isSaving}
              >
                Save Settings
              </Button>
            </HStack>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
};
